import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Alert, Card, Form, Input, Button, Typography, Select } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, IdcardOutlined, BankOutlined } from '@ant-design/icons';
import { useUser } from '../contexts/UserContext';
import {
  loginHrefWithNext,
  normalizeNextPath,
  POST_REGISTER_PATH,
} from '../utils/onboardingNavigation';
import {
  EmailMfaChallengeForm,
  type EmailMfaStep,
} from '../components/Auth/EmailMfaChallengeForm';
import { clearPendingMfaToken, getPendingMfaToken, pendingMfaStepFromToken } from '../services/authTokenRefresh';
import { getApiErrorMessage } from '../utils/apiErrors';
import { mfaLoginEmailHint, parseMfaResendResult, applyMfaResendFeedback } from '../utils/mfaEmailHints';

/**
 * Signup → one password auth → one MFA OTP → prospector (when MFA subsumes email-link verify).
 * Pending MFA is restored from sessionStorage if the route tree remounts after login.
 */
export const RegisterPage: React.FC = () => {
  const { register, login, verifyMfaOtp, resendMfaOtp, isAuthenticated } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get('next');
  const postRegisterPath = normalizeNextPath(nextParam, POST_REGISTER_PATH);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState<{ emailVerified: boolean; email: string } | null>(null);
  const [mfaStep, setMfaStep] = useState<EmailMfaStep | null>(() =>
    pendingMfaStepFromToken(getPendingMfaToken())
  );
  const [resendHint, setResendHint] = useState<string | null>(null);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState<number | null>(null);
  const [resendCooldownToken, setResendCooldownToken] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(postRegisterPath, { replace: true });
    }
  }, [isAuthenticated, navigate, postRegisterPath]);

  const onFinish = async (values: {
    username: string;
    email: string;
    password: string;
    full_name?: string;
    company_name?: string;
    industry?: string;
    headcount?: number;
  }) => {
    setLoading(true);
    setFormError(null);
    setResendHint(null);
    setResendCooldownSeconds(null);
    try {
      const result = await register({
        username: values.username,
        email: values.email,
        password: values.password,
        full_name: values.full_name,
        company_name: values.company_name,
        industry: values.industry,
        headcount: values.headcount,
      });
      if (result.emailVerified) {
        const loginResult = await login(values.email, values.password);
        if (loginResult.mfaRequired) {
          setMfaStep(loginResult.mfaStep);
          setResendHint(mfaLoginEmailHint(loginResult.mfaEmailSent));
          return;
        }
        navigate(postRegisterPath, { replace: true });
        return;
      }
      setDone({ emailVerified: result.emailVerified, email: result.email });
    } catch (err: unknown) {
      setFormError(getApiErrorMessage(err, 'Could not create account. Try again.'));
    } finally {
      setLoading(false);
    }
  };

  const onVerifyOtp = async (otp: string) => {
    setLoading(true);
    try {
      await verifyMfaOtp(otp);
      navigate(postRegisterPath, { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    setResendLoading(true);
    setResendHint(null);
    try {
      const result = await resendMfaOtp();
      applyMfaResendFeedback(parseMfaResendResult(result), {
        setHint: setResendHint,
        setCooldownSeconds: setResendCooldownSeconds,
        bumpCooldownToken: () => setResendCooldownToken((t) => t + 1),
      });
    } catch (err: unknown) {
      setResendCooldownSeconds(null);
      setResendHint(getApiErrorMessage(err, 'Could not resend code. Try again.'));
    } finally {
      setResendLoading(false);
    }
  };

  const backFromMfa = () => {
    clearPendingMfaToken();
    setMfaStep(null);
    setResendHint(null);
    setResendCooldownSeconds(null);
  };

  if (mfaStep) {
    return (
      <div style={{ maxWidth: 480, margin: '48px auto', padding: 24 }}>
        <Card>
          <Typography.Title level={4} style={{ marginTop: 0 }}>
            Verify your email
          </Typography.Title>
          <Typography.Paragraph type="success" style={{ marginBottom: 16 }}>
            Account created. Enter the one code we just emailed you — that finishes signup and opens
            intake (no second sign-in).
          </Typography.Paragraph>
          <EmailMfaChallengeForm
            key={mfaStep}
            mfaStep={mfaStep}
            loading={loading}
            resendLoading={resendLoading}
            resendHint={resendHint}
            resendCooldownSeconds={resendCooldownSeconds}
            resendCooldownToken={resendCooldownToken}
            onResendCooldownFinished={() => setResendCooldownSeconds(null)}
            onVerify={onVerifyOtp}
            onResend={onResend}
            onBack={backFromMfa}
            backLabel="Back to create account"
          />
        </Card>
      </div>
    );
  }

  if (done) {
    const loginTo = loginHrefWithNext(postRegisterPath, { registered: true });
    return (
      <div style={{ maxWidth: 480, margin: '48px auto', padding: 24 }}>
        <Card>
          <Typography.Title level={4} style={{ marginTop: 0 }}>
            Check your email
          </Typography.Title>
          <p>
            We sent a verification link to <strong>{done.email}</strong>. Open that link to confirm your
            address — you&apos;ll continue to sign in once, then intake.
          </p>
          <p style={{ color: '#6b7280', fontSize: 14 }}>
            If you don&apos;t see the message, check spam or promotions.
          </p>
          <Button type="primary" onClick={() => navigate(loginTo)}>
            I already verified — continue to sign in
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: '48px auto', padding: 24 }}>
      <Card title="Create account">
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          After you create your account you&apos;ll continue in company intake (prospector).
        </Typography.Paragraph>
        {formError && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={formError}
            action={
              /already registered/i.test(formError) ? (
                <Button size="small" type="link" onClick={() => navigate(loginHrefWithNext(postRegisterPath, { registered: true }))}>
                  Sign in instead
                </Button>
              ) : undefined
            }
          />
        )}
        <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: 'Choose a username' }]}
          >
            <Input prefix={<IdcardOutlined />} placeholder="Username" size="large" autoComplete="username" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="Email" size="large" autoComplete="email" />
          </Form.Item>
          <Form.Item name="full_name" label="Full name (optional)">
            <Input prefix={<UserOutlined />} placeholder="Full name" size="large" autoComplete="name" />
          </Form.Item>
          <Form.Item name="company_name" label="Company name" rules={[{ required: true, message: 'Company name is required for intake' }]}>
            <Input prefix={<BankOutlined />} placeholder="Your company" size="large" autoComplete="organization" />
          </Form.Item>
          <Form.Item name="industry" label="Industry">
            <Select
              placeholder="Select industry"
              size="large"
              allowClear
              options={[
                { value: 'software', label: 'Software' },
                { value: 'hardware', label: 'Hardware' },
                { value: 'services', label: 'Services' },
                { value: 'franchise', label: 'Franchise' },
                { value: 'other', label: 'Other' },
              ]}
            />
          </Form.Item>
          <Form.Item name="headcount" label="Approximate headcount">
            <Select
              placeholder="Select range"
              size="large"
              allowClear
              options={[
                { value: 5, label: '1–5' },
                { value: 15, label: '6–25' },
                { value: 50, label: '26–100' },
                { value: 150, label: '100+' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter a password' },
              { min: 8, message: 'Use at least 8 characters' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              Create account
            </Button>
          </Form.Item>
        </Form>
        <div style={{ textAlign: 'center' }}>
          <Link to={loginHrefWithNext(nextParam)}>
            Already have an account? Sign in
          </Link>
          {' · '}
          <Link to="/">Home</Link>
        </div>
      </Card>
    </div>
  );
};
